import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import {
  copyAccommodationAsync,
  CopyAccommodationCommand,
} from '../accommodationsSlice';
import { addStatusToast } from '../../../common/components';

interface CopyAccommodationModalProps {
  show: boolean;
  onHide: () => void;
  sourceAccommodationId: string;
  accommodationNumber: number;
}

const CopyAccommodationModal: React.FC<CopyAccommodationModalProps> = ({
  show,
  onHide,
  sourceAccommodationId,
  accommodationNumber,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [options, setOptions] = useState({
    copyAddons: false,
    copyOwners: false,
    copyMaintenance: false,
    copyBuildings: false,
  });

  const handleCopy = async () => {
    setLoading(true);
    const command: CopyAccommodationCommand = {
      sourceAccommodationId,
      ...options,
    };

    try {
      const resultAction = await dispatch(copyAccommodationAsync(command));
      if (copyAccommodationAsync.fulfilled.match(resultAction)) {
        dispatch(
          addStatusToast({
            Message: 'Kohde kopioitu onnistuneesti',
            IsFailed: false,
            Title: 'Onnistui',
          }),
        );
        onHide();
        // Ohjaus uuden kohteen muokkaussivulle.
        // Oletetaan että backendi palauttaa uuden kohteen ID:n tai se on resultAction.payload.result.id
        const newId = resultAction.payload?.result;
        console.debug(
          'New accommodation Result:',
          resultAction.payload?.result,
        );
        if (newId) {
          navigate(`/accommodations/details/${newId}`);
        }
      } else {
        dispatch(
          addStatusToast({
            Message: 'Virhe kohteen kopioinnissa',
            IsFailed: true,
            Title: 'Virhe',
          }),
        );
      }
    } catch (error) {
      console.error('Copy failed', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Kopioi kohde {accommodationNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <h5>Kopioitavat tiedot</h5>
          <p>Seuraavat tiedot kopioidaan aina:</p>
          <ul>
            <li>Perustiedot (nimi, tyyppi, sijainti)</li>
            <li>Kuvaukset ja käännökset</li>
            <li>Ominaisuudet (attribuutit)</li>
          </ul>
          <p>
            Seuraavia tietoja <strong>ei</strong> kopioida:
          </p>
          <ul>
            <li>Hinnat</li>
            <li>Kuvat</li>
            <li>Varaustilanne</li>
          </ul>
        </Alert>

        <Form>
          <h5>Valinnaiset kopioitavat tiedot</h5>
          <Row>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                id="copyAddons"
                label="Lisäpalvelut"
                checked={options.copyAddons}
                onChange={e =>
                  setOptions({ ...options, copyAddons: e.target.checked })
                }
              />
              <Form.Check
                type="checkbox"
                id="copyOwners"
                label="Omistajat"
                checked={options.copyOwners}
                onChange={e =>
                  setOptions({ ...options, copyOwners: e.target.checked })
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                id="copyMaintenance"
                label="Huoltajat"
                checked={options.copyMaintenance}
                onChange={e =>
                  setOptions({ ...options, copyMaintenance: e.target.checked })
                }
              />
              <Form.Check
                type="checkbox"
                id="copyBuildings"
                label="Rakennukset ja tilat"
                checked={options.copyBuildings}
                onChange={e =>
                  setOptions({ ...options, copyBuildings: e.target.checked })
                }
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Peruuta
        </Button>
        <Button variant="primary" onClick={handleCopy} disabled={loading}>
          {loading ? 'Kopioidaan...' : 'Kopioi kohde'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CopyAccommodationModal;
